import React, { useState, useEffect, useCallback } from 'react';
import gmbApi from '../services/gmb';
import permissionsApi from '../services/permissions';
import GmbLocationDetail from './GmbLocationDetail';
import './GoogleAvis.css';

// ─── Helpers d'affichage ─────────────────────────────────────────────────────

function Stars({ rating }) {
  const n = Number(rating) || 0;
  return (
    <span className={`gmb-stars gmb-stars-${n}`} title={`${n}/5`}>
      {'★'.repeat(n)}{'☆'.repeat(5 - n)}
    </span>
  );
}

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
}

// Les avis Google sont parfois renvoyés au format « (Translated by Google) … (Original) … ».
// On ne garde que le texte ORIGINAL (on ne travaille qu'en français).
function originalComment(text) {
  if (!text) return text;
  const m = text.match(/\((?:Original|Avis d'origine)\)\s*([\s\S]*)$/i);
  if (m) return m[1].trim();
  // Cas « (Translated by Google) … » sans marqueur d'origine → on retire l'en-tête de traduction.
  return text.replace(/^\s*\((?:Translated by Google|Traduit par Google)\)\s*/i, '').trim();
}

// Étoiles fractionnaires (pour les moyennes) : piste grise + remplissage proportionnel.
function StarRating({ value }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <span className="gmb-stars-avg" title={`${v.toFixed(1)} / 5`} aria-label={`${v.toFixed(1)} sur 5`}>
      <span className="gmb-stars-avg-bg">★★★★★</span>
      <span className="gmb-stars-avg-fill" style={{ width: `${(v / 5) * 100}%` }}>★★★★★</span>
    </span>
  );
}

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MONTHS_FR[Number(m) - 1] || ''} ${String(y).slice(2)}`;
}

// Présets de période adaptés à un tableau de bord réputation (buckets mensuels).
const GMB_PRESETS = [
  { value: '1m', label: '1 mois', months: 1 },
  { value: '3m', label: '3 mois', months: 3 },
  { value: '6m', label: '6 mois', months: 6 },
  { value: '12m', label: '12 mois', months: 12 },
  { value: '24m', label: '24 mois', months: 24 },
  { value: 'all', label: 'Tout', months: null },
];
function rangeForPreset(preset) {
  const def = GMB_PRESETS.find((p) => p.value === preset);
  if (!def || def.months == null) return { from: null, to: null };
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - def.months);
  from.setDate(1);
  from.setHours(0, 0, 0, 0);
  return { from: from.toISOString(), to: to.toISOString() };
}

// Puce de tendance (Δ) : flèche + valeur, colorée selon le sens.
function DeltaChip({ delta, suffix = '', invert = false }) {
  if (delta == null || !isFinite(delta)) return <span className="gmb-delta gmb-delta-flat">— réf.</span>;
  const rounded = Math.round(delta * 10) / 10;
  if (rounded === 0) return <span className="gmb-delta gmb-delta-flat">＝ stable</span>;
  const positive = invert ? rounded < 0 : rounded > 0;
  const arrow = rounded > 0 ? '▲' : '▼';
  return (
    <span className={`gmb-delta ${positive ? 'gmb-delta-up' : 'gmb-delta-down'}`}>
      {arrow} {rounded > 0 ? '+' : ''}{rounded}{suffix}
    </span>
  );
}

// ─── Graphe d'évolution : volume (barres) + note moyenne (courbe), sans librairie ──
function EvolutionChart({ timeline }) {
  if (!timeline || timeline.length === 0) {
    return <div className="gmb-empty">Aucun avis sur la période pour tracer une évolution.</div>;
  }
  const COL = 58, H = 240, padTop = 30, padBottom = 30;
  const W = Math.max(timeline.length * COL, 280);
  const plotTop = padTop, plotBottom = H - padBottom, plotH = plotBottom - plotTop;
  const maxCount = Math.max(1, ...timeline.map((t) => t.count));
  const x = (i) => i * COL + COL / 2;
  const yRating = (v) => plotBottom - (Math.max(0, Math.min(5, v)) / 5) * plotH;

  const ratingPts = timeline
    .map((t, i) => (t.avg_rating != null ? { i, v: t.avg_rating } : null))
    .filter(Boolean);
  const linePath = ratingPts.map((p) => `${x(p.i)},${yRating(p.v)}`).join(' ');

  return (
    <div className="gmb-chart-wrap">
      <div className="gmb-chart-legend">
        <span><i className="gmb-lg-bar" /> Volume d'avis</span>
        <span><i className="gmb-lg-line" /> Note moyenne (/5)</span>
      </div>
      <div className="gmb-chart-scroll">
        <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="gmb-chart-svg" role="img">
          {/* Lignes de repère note 1..5 */}
          {[1, 2, 3, 4, 5].map((g) => (
            <line key={g} x1={0} x2={W} y1={yRating(g)} y2={yRating(g)} className="gmb-grid" />
          ))}
          {/* Barres de volume */}
          {timeline.map((t, i) => {
            const bh = (t.count / maxCount) * plotH;
            return (
              <g key={t.month}>
                <rect
                  x={x(i) - COL * 0.26} y={plotBottom - bh}
                  width={COL * 0.52} height={Math.max(bh, t.count > 0 ? 2 : 0)}
                  rx={3} className="gmb-bar"
                >
                  <title>{`${monthLabel(t.month)} — ${t.count} avis${t.avg_rating != null ? `, note moy. ${t.avg_rating}` : ''}`}</title>
                </rect>
                {t.count > 0 && (
                  <text x={x(i)} y={plotBottom - bh - 6} className="gmb-bar-label">{t.count}</text>
                )}
                <text x={x(i)} y={H - 10} className="gmb-x-label">{monthLabel(t.month)}</text>
              </g>
            );
          })}
          {/* Courbe note moyenne */}
          {ratingPts.length > 1 && <polyline points={linePath} className="gmb-line" />}
          {ratingPts.map((p) => (
            <g key={`d${p.i}`}>
              <circle cx={x(p.i)} cy={yRating(p.v)} r={4} className="gmb-dot" />
              <text x={x(p.i)} y={yRating(p.v) - 9} className="gmb-dot-label">{p.v.toFixed(1)}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}

// ─── Répartition des notes (5★ → 1★) ─────────────────────────────────────────
function RatingBreakdown({ distribution, total }) {
  return (
    <div className="gmb-breakdown">
      {distribution.map((d) => {
        const pct = total > 0 ? (d.count / total) * 100 : 0;
        return (
          <div className="gmb-bd-row" key={d.rating}>
            <span className="gmb-bd-label">{d.rating} ★</span>
            <span className="gmb-bd-track">
              <span className={`gmb-bd-fill gmb-bd-${d.rating}`} style={{ width: `${pct}%` }} />
            </span>
            <span className="gmb-bd-count">{d.count}</span>
            <span className="gmb-bd-pct">{pct.toFixed(0)}%</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Onglet Tableau de bord ──────────────────────────────────────────────────
function GmbDashboard({ user }) {
  const [preset, setPreset] = useState('1m');
  // Concession : celle choisie dans le menu (vide = toutes).
  const [fConcession] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [fLocation, setFLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { from, to } = rangeForPreset(preset);
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      if (fConcession) params.concession_id = fConcession;
      if (fLocation) params.location_id = fLocation;
      const res = await gmbApi.getStats(params);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement du tableau de bord.');
    } finally { setLoading(false); }
  }, [preset, fConcession, fLocation]);

  useEffect(() => { load(); }, [load]);

  // Liste des fiches (pour le filtre par alias) — chargée une fois, indépendante des filtres.
  useEffect(() => {
    gmbApi.getLocations().then((r) => setLocations(r.data.locations || [])).catch(() => {});
  }, []);

  const aliasOptions = locations
    .filter((l) => !fConcession || String(l.concession_id) === String(fConcession))
    .map((l) => ({ id: l.id, label: l.alias || l.title || `Fiche ${l.id}` }))
    .sort((a, b) => a.label.localeCompare(b.label));

  if (loading && !data) return <div className="gmb-empty">Chargement du tableau de bord…</div>;
  if (error) return <div className="status-error gmb-error">{error}</div>;
  if (!data) return null;

  const { cumulative, period, previous, distribution, timeline, by_concession, by_location = [] } = data;
  const cumTotal = cumulative.total || 0;

  if (cumTotal === 0) {
    return (
      <div className="gmb-empty">
        Aucun avis pour l'instant. Lancez une synchronisation (bouton <strong>🔄 Synchroniser</strong>) une fois les fiches activées.
      </div>
    );
  }

  // Tendances
  const volDelta = previous.total != null && previous.total > 0
    ? ((period.total - previous.total) / previous.total) * 100 : null;
  const avgDelta = previous.avg_rating != null && period.avg_rating != null
    ? period.avg_rating - previous.avg_rating : null;
  const replyRate = period.total > 0 ? (period.replied / period.total) * 100 : 0;
  const cumReplyRate = cumTotal > 0 ? ((cumulative.replied || 0) / cumTotal) * 100 : 0;
  const unanswered = cumulative.unanswered || 0;

  // Insights automatiques (uniquement positifs)
  const insights = [];
  if (avgDelta != null && avgDelta >= 0.1) {
    insights.push({ tone: 'success', text: `📈 Vos notes récentes progressent (+${avgDelta.toFixed(1)}★ vs période précédente).` });
  }
  if (unanswered === 0 && cumTotal > 0) {
    insights.push({ tone: 'success', text: '✅ Tous les avis ont une réponse. Excellent pour votre réputation !' });
  }

  return (
    <div className="gmb-dash">
      {/* Filtres */}
      <div className="page-toolbar gmb-dash-toolbar">
        <div className="gmb-period-tabs">
          {GMB_PRESETS.map((p) => (
            <button key={p.value} className={preset === p.value ? 'active' : ''} onClick={() => setPreset(p.value)}>
              {p.label}
            </button>
          ))}
        </div>
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        {aliasOptions.length > 0 && (
          <select className={`page-select ${fLocation ? 'active-filter' : ''}`} value={fLocation} onChange={(e) => setFLocation(e.target.value)}>
            <option value="">Toutes les fiches (alias)</option>
            {aliasOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        )}
      </div>

      {/* Cartes KPI */}
      <div className="gmb-kpi-grid">
        <div className="gmb-kpi gmb-kpi-hero">
          <div className="gmb-kpi-label">Note moyenne globale</div>
          <div className="gmb-kpi-value">{cumulative.avg_rating != null ? cumulative.avg_rating.toFixed(1) : '—'}</div>
          <StarRating value={cumulative.avg_rating} />
          <div className="gmb-kpi-sub">sur {cumTotal} avis au total</div>
        </div>

        <div className="gmb-kpi">
          <div className="gmb-kpi-label">Nouveaux avis</div>
          <div className="gmb-kpi-value">{period.total}</div>
          <DeltaChip delta={volDelta} suffix="%" />
          <div className="gmb-kpi-sub">{period.with_comment} avec commentaire</div>
        </div>

        <div className="gmb-kpi">
          <div className="gmb-kpi-label">Note moyenne (période)</div>
          <div className="gmb-kpi-value">{period.avg_rating != null ? period.avg_rating.toFixed(1) : '—'}</div>
          <DeltaChip delta={avgDelta} suffix="★" />
          <div className="gmb-kpi-sub">tendance vs période précédente</div>
        </div>

        <div className="gmb-kpi">
          <div className="gmb-kpi-label">Taux de réponse</div>
          <div className="gmb-kpi-value">{replyRate.toFixed(0)}<span className="gmb-kpi-unit">%</span></div>
          <div className="gmb-kpi-progress"><span style={{ width: `${replyRate}%` }} /></div>
          <div className={`gmb-kpi-sub ${unanswered > 0 ? 'gmb-kpi-warn' : ''}`}>
            {unanswered > 0 ? `${unanswered} à répondre (au total)` : 'tout est répondu'}
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="gmb-insights">
          {insights.map((ins, i) => (
            <div key={i} className={`gmb-insight gmb-insight-${ins.tone}`}>{ins.text}</div>
          ))}
        </div>
      )}

      {/* Évolution + Répartition */}
      <div className="gmb-dash-cols">
        <div className="table-container gmb-card">
          <div className="gmb-card-title">Évolution des avis</div>
          <EvolutionChart timeline={timeline} />
        </div>
        <div className="table-container gmb-card">
          <div className="gmb-card-title">Répartition des notes <span className="gmb-card-sub">(période)</span></div>
          <RatingBreakdown distribution={distribution} total={period.total} />
        </div>
      </div>

      {/* Classement des sociétés (dépliable par fiche) */}
      {by_concession.length > 1 && (
        <div className="table-container gmb-card">
          <div className="gmb-card-title">Classement des sociétés <span className="gmb-card-sub">(période — cliquez une ligne pour voir les fiches)</span></div>
          <table className="gmb-rank-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Société</th>
                <th>Note moyenne</th>
                <th style={{ textAlign: 'center' }}>Avis</th>
                <th>Taux de réponse</th>
              </tr>
            </thead>
            <tbody>
              {by_concession.map((c, i) => {
                const rr = c.count > 0 ? (c.replied / c.count) * 100 : 0;
                const key = c.concession_id || `none-${i}`;
                const fiches = by_location.filter((l) => String(l.concession_id || '') === String(c.concession_id || ''));
                const isOpen = !!expanded[key];
                return (
                  <React.Fragment key={key}>
                    <tr className="gmb-rank-parent" onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))}>
                      <td className="gmb-rank-pos">{i + 1}</td>
                      <td>
                        <span className="gmb-rank-caret">{fiches.length > 1 ? (isOpen ? '▾' : '▸') : ''}</span>
                        <strong>{c.concession_nom || <span className="status-warning">Non rattachée</span>}</strong>
                        {fiches.length > 1 && <span className="gmb-rank-count">{fiches.length} fiches</span>}
                      </td>
                      <td>
                        <span className="gmb-rank-rating">
                          <StarRating value={c.avg_rating} />
                          <b>{c.avg_rating != null ? c.avg_rating.toFixed(1) : '—'}</b>
                        </span>
                      </td>
                      <td style={{ textAlign: 'center' }}>{c.count}</td>
                      <td>
                        <span className="gmb-rank-reply">
                          <span className="gmb-rank-bar"><span style={{ width: `${rr}%` }} /></span>
                          {rr.toFixed(0)}%
                          {c.unanswered > 0 && <span className="gmb-rank-todo">{c.unanswered} à répondre</span>}
                        </span>
                      </td>
                    </tr>
                    {isOpen && fiches.map((l) => {
                      const lrr = l.count > 0 ? (l.replied / l.count) * 100 : 0;
                      return (
                        <tr className="gmb-rank-sub" key={`${key}-${l.location_id}`}>
                          <td />
                          <td className="gmb-rank-sub-name">{l.location_alias || l.location_title || '(sans titre)'}</td>
                          <td>
                            <span className="gmb-rank-rating">
                              <StarRating value={l.avg_rating} />
                              <b>{l.avg_rating != null ? l.avg_rating.toFixed(1) : '—'}</b>
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>{l.count}</td>
                          <td>
                            <span className="gmb-rank-reply">
                              <span className="gmb-rank-bar"><span style={{ width: `${lrr}%` }} /></span>
                              {lrr.toFixed(0)}%
                              {l.unanswered > 0 && <span className="gmb-rank-todo">{l.unanswered} à répondre</span>}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Modale de réponse ───────────────────────────────────────────────────────

function GoogleReplyModal({ review, onClose, onSent }) {
  const [text, setText] = useState(review.reply_comment || '');
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState('');

  const handleDraft = async () => {
    setDrafting(true); setError('');
    try {
      const { data } = await gmbApi.draftReply(review.id);
      setText(data.draft || '');
    } catch (e) {
      setError(e.response?.data?.error || 'Génération IA indisponible.');
    } finally { setDrafting(false); }
  };

  const handleSend = async () => {
    if (!text.trim()) return;
    setSending(true); setError('');
    try {
      const { data } = await gmbApi.reply(review.id, text.trim());
      onSent(data.review);
    } catch (e) {
      setError(e.response?.data?.error || 'Échec de la publication sur Google.');
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Répondre à l'avis</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="gmb-review-card">
            <div className="gmb-review-head">
              <strong>{review.author_name}</strong>
              <Stars rating={review.rating} />
              <span className="gmb-review-date">{fmtDate(review.create_time)}</span>
            </div>
            <div className="gmb-review-meta">
              {review.concession_nom || 'Concession non rattachée'}
              {review.location_title ? ` — ${review.location_title}` : ''}
            </div>
            <p className="gmb-review-comment">{originalComment(review.comment) || <em>(note sans commentaire)</em>}</p>
          </div>

          <div className="form-group full">
            <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Votre réponse publique</span>
              <button type="button" className="btn-secondary gmb-ai-btn" onClick={handleDraft} disabled={drafting || sending}>
                {drafting ? '… génération' : '🪄 Brouillon IA'}
              </button>
            </label>
            <textarea
              className="gmb-reply-textarea"
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Rédigez votre réponse, ou générez un brouillon avec l'IA puis ajustez-le…"
            />
          </div>

          {error && <div className="status-error gmb-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="btn-primary" onClick={handleSend} disabled={sending || !text.trim()}>
            {sending ? 'Publication…' : 'Publier sur Google'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Configuration (super-admin) ──────────────────────────────────────

function GmbConfig({ concessions, isSuperAdmin }) {
  const [status, setStatus] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [st, loc] = await Promise.all([
        isSuperAdmin ? gmbApi.authStatus() : Promise.resolve({ data: null }),
        gmbApi.getLocations(),
      ]);
      if (st.data) setStatus(st.data);
      setLocations(loc.data.locations || []);
    } catch (e) {
      setMsg(e.response?.data?.error || 'Erreur de chargement.');
    } finally { setLoading(false); }
  }, [isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleConnect = async () => {
    try {
      const { data } = await gmbApi.authUrl();
      window.location.href = data.url;
    } catch (e) {
      setMsg(e.response?.data?.error || 'Connexion impossible — vérifiez la configuration OAuth (.env).');
    }
  };

  const handleRefresh = async () => {
    setBusy('refresh'); setMsg('');
    try {
      const { data } = await gmbApi.refreshLocations();
      setMsg(`${data.imported} fiche(s) importée(s) / mise(s) à jour.`);
      await load();
    } catch (e) {
      setMsg(e.response?.data?.error || 'Échec de l\'import des fiches.');
    } finally { setBusy(''); }
  };

  const updateLoc = async (id, patch) => {
    try {
      const { data } = await gmbApi.updateLocation(id, patch);
      setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, ...data.location } : l)));
    } catch (e) {
      setMsg(e.response?.data?.error || 'Mise à jour impossible.');
    }
  };

  if (loading) return <div className="gmb-empty">Chargement…</div>;

  return (
    <div className="gmb-config">
      {isSuperAdmin && (
        <div className="table-container gmb-conn-card">
          <div className="gmb-conn-row">
            <div>
              <div className="gmb-conn-title">Connexion Google Business Profile</div>
              <div className="gmb-conn-sub">
                {!status?.configured && <span className="status-warning">Configuration OAuth incomplète (.env)</span>}
                {status?.configured && status?.connected && (
                  <span className="status-success">Connecté{status.account?.label ? ` — ${status.account.label}` : ''}</span>
                )}
                {status?.configured && !status?.connected && <span className="status-info">Aucun compte connecté</span>}
              </div>
            </div>
            <div className="action-buttons">
              <button className="btn-secondary" onClick={handleConnect} disabled={!status?.configured}>
                {status?.connected ? 'Reconnecter' : 'Connecter un compte Google'}
              </button>
              <button className="btn-primary" onClick={handleRefresh} disabled={!status?.connected || busy === 'refresh'}>
                {busy === 'refresh' ? 'Import…' : 'Importer les fiches'}
              </button>
            </div>
          </div>
        </div>
      )}

      {msg && <div className="status-info gmb-msg">{msg}</div>}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Fiche Google</th>
              <th>Adresse</th>
              <th>Concession rattachée</th>
              <th>Synchro</th>
              <th>Dernière synchro</th>
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 && (
              <tr><td colSpan={5} className="gmb-empty">Aucune fiche. Connectez un compte puis « Importer les fiches ».</td></tr>
            )}
            {locations.map((l) => (
              <tr key={l.id}>
                <td><strong>{l.title || '(sans titre)'}</strong></td>
                <td>{l.address || '—'}</td>
                <td>
                  <select
                    className="page-select"
                    value={l.concession_id || ''}
                    disabled={!isSuperAdmin}
                    onChange={(e) => updateLoc(l.id, { concession_id: e.target.value ? Number(e.target.value) : null })}
                  >
                    <option value="">— Non rattachée —</option>
                    {concessions.map((c) => <option key={c.id} value={c.id}>{c.nom}</option>)}
                  </select>
                </td>
                <td>
                  <label className="gmb-switch">
                    <input
                      type="checkbox"
                      checked={!!l.enabled}
                      disabled={!isSuperAdmin}
                      onChange={(e) => updateLoc(l.id, { enabled: e.target.checked })}
                    />
                    {l.enabled ? 'Activée' : 'Désactivée'}
                  </label>
                </td>
                <td>{fmtDate(l.last_synced_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="gmb-hint">
        La synchronisation ne traite que les fiches <strong>activées</strong> rattachées à une concession dont l'option
        « Avis Google » est cochée (Administration → Concessions). Réglez la fréquence dans <strong>Bases de données → Synchronisations</strong> (clé <code>gmb_reviews</code>).
      </p>
    </div>
  );
}

// ─── Onglet Fiches : toutes les pages Google + note globale ──────────────────
function noteTier(v) {
  if (v == null) return '';
  if (v >= 4.5) return 'gmb-note-good';
  if (v >= 3.5) return 'gmb-note-ok';
  return 'gmb-note-bad';
}

function GmbLocations({ user, canManage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('rating');
  const [fConcession] = useState(() => (user?.concession_id ? String(user.concession_id) : '')); // concession du menu
  const [detailLoc, setDetailLoc] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const res = await gmbApi.getLocationsOverview();
        setData(res.data);
      } catch (e) {
        setError(e.response?.data?.error || 'Erreur de chargement des fiches.');
      } finally { setLoading(false); }
    })();
  }, []);

  const saveAlias = async (id, value) => {
    try {
      await gmbApi.updateLocation(id, { alias: value || null });
      setData((prev) => ({ ...prev, locations: prev.locations.map((l) => (l.id === id ? { ...l, alias: value || null } : l)) }));
    } catch (e) {
      setError(e.response?.data?.error || "Enregistrement de l'alias impossible.");
    }
  };

  if (loading) return <div className="gmb-empty">Chargement des fiches…</div>;
  if (error) return <div className="status-error gmb-error">{error}</div>;
  if (!data) return null;
  if (data.locations.length === 0) {
    return <div className="gmb-empty">Aucune fiche Google importée. Voir l'onglet Configuration.</div>;
  }

  let locations = [...data.locations];
  if (fConcession) {
    locations = locations.filter((l) => String(l.concession_id || '') === String(fConcession));
  }
  if (q.trim()) {
    const needle = q.trim().toLowerCase();
    locations = locations.filter((l) =>
      (l.title || '').toLowerCase().includes(needle) ||
      (l.address || '').toLowerCase().includes(needle) ||
      (l.concession_nom || '').toLowerCase().includes(needle));
  }
  if (sort === 'volume') locations.sort((a, b) => b.reviews_count - a.reviews_count);
  else if (sort === 'name') locations.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
  else locations.sort((a, b) => (b.avg_rating || 0) - (a.avg_rating || 0) || b.reviews_count - a.reviews_count);

  // Récap recalculé sur l'ensemble affiché (cohérent avec les filtres actifs).
  const totalReviews = locations.reduce((s, l) => s + l.reviews_count, 0);
  const weighted = locations.reduce((s, l) => s + (l.avg_rating || 0) * l.reviews_count, 0);
  const summary = {
    locations_count: locations.length,
    total_reviews: totalReviews,
    group_avg_rating: totalReviews > 0 ? Math.round((weighted / totalReviews) * 100) / 100 : null,
    total_unanswered: locations.reduce((s, l) => s + l.unanswered, 0),
  };

  return (
    <div className="gmb-loc">
      {/* Bandeau récap groupe */}
      <div className="gmb-loc-summary">
        <div className="gmb-loc-sum-main">
          <div className={`gmb-loc-sum-value ${noteTier(summary.group_avg_rating)}`}>
            {summary.group_avg_rating != null ? summary.group_avg_rating.toFixed(1) : '—'}
          </div>
          <div>
            <StarRating value={summary.group_avg_rating} />
            <div className="gmb-loc-sum-label">
              {fConcession ? 'Note moyenne' : 'Note globale du groupe'} — {summary.total_reviews} avis sur {summary.locations_count} fiche{summary.locations_count > 1 ? 's' : ''}
            </div>
          </div>
        </div>
        {summary.total_unanswered > 0 && (
          <div className="gmb-loc-sum-todo">{summary.total_unanswered} avis à répondre</div>
        )}
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <input className="page-search" placeholder="🔍 Rechercher une fiche…" value={q} onChange={(e) => setQ(e.target.value)} />
        {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
        <select className="page-select" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="rating">Trier : meilleure note</option>
          <option value="volume">Trier : nombre d'avis</option>
          <option value="name">Trier : nom (A→Z)</option>
        </select>
      </div>

      {/* Tableau des fiches */}
      <div className="table-container">
        <table className="gmb-loc-table">
          <thead>
            <tr>
              <th>Fiche Google</th>
              <th>Alias (interne)</th>
              <th>Concession</th>
              <th>Note moyenne</th>
              <th style={{ textAlign: 'center' }}>Avis</th>
              <th>Taux de réponse</th>
              <th style={{ textAlign: 'center' }}>À répondre</th>
              {canManage && <th style={{ width: 40 }} />}
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 && (
              <tr><td colSpan={canManage ? 8 : 7} className="gmb-empty">Aucune fiche ne correspond à la recherche.</td></tr>
            )}
            {locations.map((l) => {
              const rr = l.reviews_count > 0 ? (l.replied / l.reviews_count) * 100 : 0;
              return (
                <tr key={l.id} className={!l.enabled ? 'gmb-loc-off' : ''}>
                  <td>
                    <div className="gmb-loc-cell-title">
                      <strong>{l.title || '(sans titre)'}</strong>
                      {!l.enabled && <span className="gmb-loc-badge">désactivée</span>}
                    </div>
                    <div className="gmb-loc-addr" title={l.address || ''}>{l.address || '—'}</div>
                  </td>
                  <td>
                    {canManage ? (
                      <input
                        className="gmb-alias-input"
                        defaultValue={l.alias || ''}
                        onBlur={(e) => { const v = e.target.value.trim(); if (v !== (l.alias || '')) saveAlias(l.id, v); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                    ) : (l.alias || <span className="gmb-muted">—</span>)}
                  </td>
                  <td>{l.concession_nom || <span className="status-warning">Non rattachée</span>}</td>
                  <td>
                    <span className="gmb-loc-rating-cell">
                      <span className={`gmb-loc-note-sm ${noteTier(l.avg_rating)}`}>
                        {l.avg_rating != null ? l.avg_rating.toFixed(1) : '—'}
                      </span>
                      <StarRating value={l.avg_rating} />
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>{l.reviews_count}</td>
                  <td>
                    <span className="gmb-rank-reply">
                      <span className="gmb-rank-bar"><span style={{ width: `${rr}%` }} /></span>
                      {rr.toFixed(0)}%
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {l.unanswered > 0 ? <span className="gmb-rank-todo">{l.unanswered}</span> : '—'}
                  </td>
                  {canManage && (
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Voir / éditer la fiche Google" onClick={() => setDetailLoc(l)}>👁️</button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {detailLoc && <GmbLocationDetail location={detailLoc} onClose={() => setDetailLoc(null)} />}
    </div>
  );
}

// ─── Onglet Prompts IA (super-admin) ─────────────────────────────────────────
// Modale d'édition d'un prompt de fiche : description humaine → raffinage Claude → édition → save.
function PromptEditorModal({ location, onClose, onSaved }) {
  const [human, setHuman] = useState(location.ai_prompt_human || '');
  const [system, setSystem] = useState(location.ai_prompt_system || '');
  const [rules, setRules] = useState('');
  const [refining, setRefining] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    gmbApi.getPromptRules().then((r) => setRules(r.data.rules || '')).catch(() => {});
  }, []);

  const handleRefine = async () => {
    if (!human.trim()) return;
    setRefining(true); setError('');
    try {
      const { data } = await gmbApi.refinePrompt(location.id, human.trim());
      setSystem(data.system || '');
    } catch (e) {
      setError(e.response?.data?.error || 'Raffinage IA indisponible.');
    } finally { setRefining(false); }
  };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const { data } = await gmbApi.savePrompt(location.id, { human: human.trim() || null, system: system.trim() || null });
      onSaved(location.id, data.location);
    } catch (e) {
      setError(e.response?.data?.error || 'Enregistrement impossible.');
      setSaving(false);
    }
  };

  const ruleLines = (rules || '')
    .split('\n')
    .map((l) => l.replace(/^[-•]\s*/, '').trim())
    .filter((l) => l && !/^RÈGLES NON/i.test(l));

  return (
    <div className="modal-overlay">
      <div className="modal-content gmb-prompt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Prompt IA — {location.alias || location.title || '(sans titre)'}</h2>
            {location.address && <div className="gmb-loc-addr">{location.address}</div>}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body gmb-prompt-body">
          {/* Bloc 1 — Règles non négociables (lecture seule, repliables) */}
          <details className="gmb-rules">
            <summary>
              <span className="gmb-rules-ico" aria-hidden="true">🔒</span>
              <span className="gmb-rules-title">Règles non négociables</span>
              <span className="gmb-rules-tag">Google · toujours appliquées</span>
            </summary>
            {ruleLines.length === 0
              ? <p className="gmb-muted gmb-rules-loading">Chargement…</p>
              : <ul className="gmb-rules-list">{ruleLines.map((l, i) => <li key={i}>{l}</li>)}</ul>}
          </details>

          {/* Bloc 2 — Contexte de l'entreprise (modifiable) */}
          <section className="gmb-ctx">
            <header className="gmb-ctx-head">
              <h3>Contexte de l'établissement</h3>
              <p>Ton, spécificités et <strong>signature</strong> propres à cette fiche. Les règles ci-dessus s'appliquent toujours en plus.</p>
            </header>

            <div className="gmb-step">
              <span className="gmb-step-num">1</span>
              <div className="gmb-step-body">
                <label className="gmb-step-label">Décris cette fiche <span className="gmb-step-sub">en langage libre</span></label>
                <textarea
                  className="gmb-reply-textarea" rows={4} value={human}
                  onChange={(e) => setHuman(e.target.value)}
                  placeholder="Ex : concession Peugeot premium à Baie-Mahault, atelier réactif. Ton chaleureux, vouvoiement strict. Mettre en avant le véhicule de courtoisie. Signer « L'équipe Peugeot Baie-Mahault »."
                />
                <div className="gmb-step-actions">
                  <button type="button" className="btn-secondary gmb-ai-btn" onClick={handleRefine} disabled={refining || !human.trim()}>
                    {refining ? '… génération' : '✨ Générer le contexte'}
                  </button>
                </div>
              </div>
            </div>

            <div className="gmb-step">
              <span className="gmb-step-num">2</span>
              <div className="gmb-step-body">
                <label className="gmb-step-label">Contexte de la fiche <span className="gmb-step-sub">éditable — guide les réponses</span></label>
                <textarea
                  className="gmb-reply-textarea" rows={9} value={system}
                  onChange={(e) => setSystem(e.target.value)}
                  placeholder="Le bloc contexte apparaît ici après génération. Tu peux aussi l'écrire ou l'ajuster manuellement."
                />
              </div>
            </div>
          </section>

          {error && <div className="status-error gmb-error">{error}</div>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
}

function GmbPrompts({ canModify = false }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { data } = await gmbApi.getLocations();
      setLocations(data.locations || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement des fiches.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSaved = (id, patch) => {
    setLocations((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    setEditing(null);
  };

  if (loading) return <div className="gmb-empty">Chargement…</div>;
  if (error) return <div className="status-error gmb-error">{error}</div>;

  return (
    <div className="gmb-prompts">
      <p className="gmb-hint">
        Un prompt personnalisé <strong>par fiche</strong> oriente le ton et le contexte des brouillons de réponse IA,
        tout en respectant les bons usages Google (anti-spam, pas de données personnelles, ton professionnel).
        Les fiches sans prompt utilisent le modèle générique par défaut.
      </p>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Fiche Google</th>
              <th>Concession</th>
              <th>Prompt IA</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {locations.length === 0 && (
              <tr><td colSpan={4} className="gmb-empty">Aucune fiche. Importez les fiches dans l'onglet Configuration.</td></tr>
            )}
            {locations.map((l) => (
              <tr key={l.id}>
                <td>
                  <div><strong>{l.title || '(sans titre)'}</strong></div>
                  <div className="gmb-loc-addr" title={l.address || ''}>{l.address || '—'}</div>
                </td>
                <td>{l.concession_nom || <span className="status-warning">Non rattachée</span>}</td>
                <td>
                  {l.ai_prompt_system
                    ? <span className="status-success">✓ Configuré</span>
                    : <span className="gmb-prompt-none">— Par défaut</span>}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-icon"
                      title={canModify ? 'Configurer le prompt' : 'Lecture seule — droit de modification requis'}
                      onClick={() => canModify && setEditing(l)}
                      disabled={!canModify}
                    >✏️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PromptEditorModal location={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
    </div>
  );
}

// ─── Composant principal ─────────────────────────────────────────────────────

function GoogleAvis({ user, concessions = [], editMode, setHeaderAction }) {
  const isSuperAdmin = user?.role === 'super-admin';
  // Onglet « Prompts IA » : permission déléguable (clé gmb_prompts).
  // checkAccess renvoie toutes les actions à true pour un super-admin.
  const [promptsPerm, setPromptsPerm] = useState({ peut_voir: false, peut_modifier: false });
  useEffect(() => {
    permissionsApi.checkAccess('gmb_prompts')
      .then((r) => setPromptsPerm(r.data?.permissions || { peut_voir: false, peut_modifier: false }))
      .catch(() => setPromptsPerm({ peut_voir: false, peut_modifier: false }));
  }, []);
  const [tab, setTab] = useState('avis');
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtres
  const [q, setQ] = useState('');
  // Concession : celle choisie dans le menu (la page est remontée à chaque changement).
  const [fConcession] = useState(() => (user?.concession_id ? String(user.concession_id) : ''));
  const [fLocation, setFLocation] = useState('');
  const [fRating, setFRating] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [locations, setLocations] = useState([]); // fiches (pour le filtre par fiche)

  const [replyTarget, setReplyTarget] = useState(null);
  const [selected, setSelected] = useState([]);

  // Synchronisation : indicateur d'avancement
  const [syncing, setSyncing] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null); // { ok, text }

  const loadReviews = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (fConcession) params.concession_id = fConcession;
      if (fLocation) params.location_id = fLocation;
      if (fRating) params.rating = fRating;
      if (fStatus) params.status = fStatus;
      if (q.trim()) params.q = q.trim();
      const { data } = await gmbApi.getReviews(params);
      setReviews(data.reviews || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Erreur de chargement des avis.');
    } finally { setLoading(false); }
  }, [fConcession, fLocation, fRating, fStatus, q]);

  useEffect(() => { if (tab === 'avis') loadReviews(); }, [tab, loadReviews]);

  // Liste des fiches (pour le filtre par fiche, dépendant de la concession).
  useEffect(() => {
    gmbApi.getLocations().then((r) => setLocations(r.data.locations || [])).catch(() => setLocations([]));
  }, []);

  // Cascade : si la fiche sélectionnée n'appartient pas à la concession choisie, on la réinitialise.
  useEffect(() => {
    if (!fLocation) return;
    const loc = locations.find((l) => String(l.id) === String(fLocation));
    if (fConcession && loc && String(loc.concession_id) !== String(fConcession)) setFLocation('');
  }, [fConcession, fLocation, locations]);

  // Bouton « Synchroniser » dans le header (admin uniquement)
  const handleSync = useCallback(async () => {
    setSyncing(true); setError(''); setSyncInfo(null);
    try {
      const r = await gmbApi.sync();
      const { count = 0, locations = 0, skipped } = r.data || {};
      setSyncInfo({
        ok: true,
        text: skipped === 'no_oauth'
          ? 'Aucun compte Google connecté — connectez un compte dans l\'onglet Configuration.'
          : `Synchronisation terminée — ${count} nouvel${count > 1 ? 's' : ''} avis sur ${locations} fiche${locations > 1 ? 's' : ''}.`,
      });
      await loadReviews();
    } catch (e) {
      setSyncInfo({ ok: false, text: e.response?.data?.error || 'Échec de la synchronisation.' });
    } finally {
      setSyncing(false);
    }
  }, [loadReviews]);

  useEffect(() => {
    if (!setHeaderAction) return;
    const canSync = isSuperAdmin || user?.role === 'admin';
    setHeaderAction(canSync ? {
      secondary: [{
        label: syncing ? '⏳ Synchronisation…' : '🔄 Synchroniser',
        onClick: syncing ? () => {} : handleSync,
      }],
    } : null);
    return () => setHeaderAction && setHeaderAction(null);
  }, [setHeaderAction, handleSync, isSuperAdmin, user, syncing]);

  // Message retour OAuth (?gmb=connected|error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('gmb') === 'connected' && isSuperAdmin) { setTab('config'); }
  }, []);

  const onReplied = (updated) => {
    setReviews((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
    setReplyTarget(null);
  };

  const openReply = async (review) => {
    setReplyTarget(review);
    if (!review.read_at) {
      gmbApi.markRead(review.id).catch(() => {});
      setReviews((prev) => prev.map((r) => (r.id === review.id ? { ...r, read_at: new Date().toISOString() } : r)));
    }
  };

  const canDelete = editMode && isSuperAdmin;
  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    if (!window.confirm(`Supprimer ${selected.length} avis du CRM ? (n'affecte pas Google)`)) return;
    try {
      await gmbApi.bulkDelete(selected);
      setReviews((prev) => prev.filter((r) => !selected.includes(r.id)));
      setSelected([]);
    } catch (e) {
      setError(e.response?.data?.error || 'Suppression impossible.');
    }
  };

  return (
    <div className="page-container">
      {/* Onglets */}
      <div className="page-tabs">
        <button className={tab === 'avis' ? 'active' : ''} onClick={() => setTab('avis')}>⭐ Avis</button>
        <button className={tab === 'dashboard' ? 'active' : ''} onClick={() => setTab('dashboard')}>📊 Tableau de bord</button>
        <button className={tab === 'fiches' ? 'active' : ''} onClick={() => setTab('fiches')}>📍 Fiches</button>
        {promptsPerm.peut_voir && (
          <button className={tab === 'prompts' ? 'active' : ''} onClick={() => setTab('prompts')}>🤖 Prompts IA</button>
        )}
        {isSuperAdmin && (
          <button className={tab === 'config' ? 'active' : ''} onClick={() => setTab('config')}>⚙️ Configuration<span className="tab-sa-lock" title="Visible par les super-admins uniquement">🔒</span></button>
        )}
      </div>

      {/* Indicateur de synchronisation (visible sur tous les onglets) */}
      {syncing && (
        <div className="gmb-sync-banner" role="status" aria-live="polite">
          <span className="gmb-sync-spinner" aria-hidden="true" />
          <div className="gmb-sync-banner-text">
            <strong>Synchronisation des avis en cours…</strong>
            <span>Récupération des avis depuis Google (cela peut prendre jusqu'à une minute).</span>
          </div>
          <div className="gmb-sync-bar"><span /></div>
        </div>
      )}
      {!syncing && syncInfo && (
        <div className={`gmb-sync-result status-${syncInfo.ok ? 'success' : 'error'}`} role="status">
          <span>{syncInfo.ok ? '✅' : '⚠️'} {syncInfo.text}</span>
          <button className="gmb-sync-dismiss" onClick={() => setSyncInfo(null)} title="Masquer">✕</button>
        </div>
      )}

      {tab === 'dashboard' ? (
        <GmbDashboard user={user} />
      ) : tab === 'fiches' ? (
        <GmbLocations user={user} canManage={isSuperAdmin} />
      ) : tab === 'prompts' && promptsPerm.peut_voir ? (
        <GmbPrompts canModify={promptsPerm.peut_modifier} />
      ) : tab === 'config' && isSuperAdmin ? (
        <GmbConfig concessions={concessions} isSuperAdmin={isSuperAdmin} />
      ) : (
        <>
          {/* Toolbar */}
          <div className="page-toolbar">
            <input
              className="page-search"
              placeholder="🔍 Rechercher..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {/* Concession : choix du menu (2026-10-26), plus de liste propre à la page. */}
            <select className={`page-select ${fLocation ? 'active-filter' : ''}`} value={fLocation} onChange={(e) => setFLocation(e.target.value)}>
              <option value="">{fConcession ? 'Toutes les fiches de la concession' : 'Toutes les fiches'}</option>
              {locations
                .filter((l) => !fConcession || String(l.concession_id) === String(fConcession))
                .map((l) => <option key={l.id} value={l.id}>{l.alias || l.title}</option>)}
            </select>
            <select className={`page-select ${fRating ? 'active-filter' : ''}`} value={fRating} onChange={(e) => setFRating(e.target.value)}>
              <option value="">Toutes les notes</option>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} étoile{n > 1 ? 's' : ''}</option>)}
            </select>
            <select className={`page-select ${fStatus ? 'active-filter' : ''}`} value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="">Tous les statuts</option>
              <option value="todo">À répondre</option>
              <option value="replied">Répondus</option>
            </select>
            {canDelete && selected.length > 0 && (
              <button className="btn-danger gmb-bulk-del" onClick={handleBulkDelete}>
                Supprimer la sélection ({selected.length})
              </button>
            )}
          </div>

          {error && <div className="status-error gmb-error">{error}</div>}

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  {canDelete && <th style={{ width: 32 }} />}
                  <th>Note</th>
                  <th>Auteur</th>
                  <th>Avis</th>
                  <th>Fiche</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={canDelete ? 8 : 7} className="gmb-empty">Chargement…</td></tr>}
                {!loading && reviews.length === 0 && (
                  <tr><td colSpan={canDelete ? 8 : 7} className="gmb-empty">Aucun avis. Vérifiez la configuration et lancez une synchronisation.</td></tr>
                )}
                {!loading && reviews.map((r) => (
                  <tr key={r.id} className={`gmb-row-click ${!r.read_at ? 'gmb-row-unread' : ''}`} onClick={() => openReply(r)}>
                    {canDelete && (
                      <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} /></td>
                    )}
                    <td><Stars rating={r.rating} /></td>
                    <td>{r.author_name}</td>
                    <td className="gmb-comment-cell" title={originalComment(r.comment) || ''}>{originalComment(r.comment) || <em>(note seule)</em>}</td>
                    <td title={r.location_title || ''}>{r.location_alias || r.location_title || '—'}</td>
                    <td>{fmtDate(r.create_time)}</td>
                    <td>
                      {r.reply_status === 'sent'
                        ? <span className="status-success">Répondu</span>
                        : <span className="status-warning">À répondre</span>}
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="action-buttons">
                        <button className="btn-icon" title="Voir / Répondre" onClick={() => openReply(r)}>✏️</button>
                        {canDelete && (
                          <button className="btn-icon delete" title="Supprimer du CRM"
                            onClick={async () => {
                              if (!window.confirm('Supprimer cet avis du CRM ? (n\'affecte pas Google)')) return;
                              await gmbApi.deleteReview(r.id);
                              setReviews((prev) => prev.filter((x) => x.id !== r.id));
                            }}>🗑️</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {replyTarget && (
        <GoogleReplyModal review={replyTarget} onClose={() => setReplyTarget(null)} onSent={onReplied} />
      )}
    </div>
  );
}

export default GoogleAvis;
